export async function optimizeImage(file: File, maxDimension: number = 1080): Promise<File> {
    return new Promise((resolve) => {
        // Only optimize images
        if (!file.type.startsWith('image/')) {
            resolve(file);
            return;
        }

        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(url);

            let width = img.width;
            let height = img.height;

            // If already within dimensions, just return original but maybe convert to progressive jpeg?
            // Actually, if it's already small, just return it to save processing time.
            if (width <= maxDimension && height <= maxDimension) {
                resolve(file);
                return;
            }

            // Calculate new dimensions
            if (width > height) {
                if (width > maxDimension) {
                    height = Math.round(height * (maxDimension / width));
                    width = maxDimension;
                }
            } else {
                if (height > maxDimension) {
                    width = Math.round(width * (maxDimension / height));
                    height = maxDimension;
                }
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                resolve(file); // Fallback to original
                return;
            }

            // Use better image scaling
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        resolve(file);
                        return;
                    }
                    // Create a new File from the blob
                    const optimizedFile = new File([blob], file.name, {
                        type: 'image/jpeg',
                        lastModified: Date.now(),
                    });
                    resolve(optimizedFile);
                },
                'image/jpeg',
                0.85 // High quality compression
            );
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(file); // Fallback to original on error
        };

        img.src = url;
    });
}

export async function createThumbnail(file: File, size: number = 128): Promise<File> {
    return new Promise((resolve) => {
        if (!file.type.startsWith('image/')) {
            resolve(file);
            return;
        }

        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(url);

            // Calculate dimensions to fit within 'size' without cropping
            let width = img.width;
            let height = img.height;

            if (width > height) {
                if (width > size) {
                    height = Math.round(height * (size / width));
                    width = size;
                }
            } else {
                if (height > size) {
                    width = Math.round(width * (size / height));
                    height = size;
                }
            }

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                resolve(file);
                return;
            }

            canvas.width = width;
            canvas.height = height;

            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
                (blob: Blob | null) => {
                    if (!blob) {
                        resolve(file);
                        return;
                    }
                    const thumbFile = new File([blob], `thumb_${file.name}`, {
                        type: 'image/jpeg',
                        lastModified: Date.now(),
                    });
                    resolve(thumbFile);
                },
                'image/jpeg',
                0.8
            );
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(file);
        };

        img.src = url;
    });
}
